<template>
  <div
    class="instagram-post"
    v-loading="loading"
    :element-loading-text="loadingText"
    element-loading-custom-class="csv-loading"
  >
    <CampaignAddWidgetDialog
      ref="campaignAddWidgetDialog"
      :campaign="campaign"
      @widget_added="fetchCampaign"
    ></CampaignAddWidgetDialog>
    <div class="mb-x2" layout="layout" layout-align="space-between-center">
      <div layout="layout">
        <div class="mr-x1">
          <el-button class="el-button-mt" type="default" @click="back"
            ><i class="mdi mdi-arrow-left"></i>戻る</el-button
          >
        </div>
      </div>
      <div v-if="campaign.id" layout-align="end-center">
        <div layout="row">
          <div class="mr-x3">応募者数: {{ formatNumber(campaign.applicantsCount) }}</div>
          <div class="mr-x3">応募総数: {{ formatNumber(entryCount) }}</div>
          <div>データ取得日時: {{ lastProcessedDatetime }}</div>
        </div>
        <div style="text-align: end">ウィジェット最終更新日時: {{ widgetModified }}</div>
      </div>
    </div>
    <el-card class="mb-x2">
      <InstagramPostFilter></InstagramPostFilter>
    </el-card>
    <el-card v-if="error">
      <div class="message message-error">
        データ取得に失敗しました。恐れ入りますが、時間を置いて再度お試しください。
      </div>
    </el-card>
    <el-card v-else-if="isLoaded" v-loading="isLoading">
      <div class="mb-x3" layout="row" layout-align="space-between-center">
        <div>
          <el-button-group>
            <el-button
              :type="isListView ? 'primary' : 'default'"
              :disabled="!posts.length"
              @click="changeViewType('list')"
              ><i class="mdi mdi-view-list"></i>一覧</el-button
            >
            <el-button
              :type="isGridView ? 'primary' : 'default'"
              :disabled="!posts.length"
              @click="changeViewType('grid')"
              ><i class="mdi mdi-view-module"></i>グリッド</el-button
            >
          </el-button-group>
          <el-select
            class="ml-x1"
            v-if="isGridView"
            v-model="sortGrid"
            size="small"
            placeholder="ソート"
            @change="onSorting(sortGrid)"
          >
            <el-option
              v-for="(item, index) of sortGridOptions"
              :key="index"
              :label="item.label"
              :value="item.value"
            ></el-option>
          </el-select>
        </div>
        <div class="header-right" layout="row">
          <el-button class="mr-x1" type="default" @click="openCampaignAddWiddetDialog"
            ><i class="mdi mdi-view-module"></i>ウィジェット管理</el-button
          >
          <CsvDownload :download="downloadCsv" :disabled="!posts.length"></CsvDownload>
        </div>
      </div>
      <div v-if="campaign.lastProcessedDatetime === null && !posts.length">
        <div class="message message-info message-center">
          <b>応募が収集されるまで、しばらくお待ちください。</b><br /><br />
          <template v-if="campaign.setting && campaign.setting.entryActionType === 'mention'">
            応募収集は、リアルタイムで収集されます。<br />
          </template>
          <template v-else> 初回の応募収集は、キャンペーン開催から1時間後です。<br /> </template>
          収集が開始されるまで、しばらくお待ちください。
          <div class="mt-x2"><img class="wait-image" src="@/client/assets/images/time.png" /></div>
        </div>
      </div>
      <div class="message message-info" v-else-if="!posts.length">
        応募がないまたはデータが収集されていません。初回のデータ収集は投稿から1時間後です。
      </div>
      <div v-else>
        <InstagramPostTable
          v-if="isListView"
          :posts="posts"
          :options="options"
          @paging="onPaging"
          @sorting="onSorting"
          @change_is_widget="changeIsWidget"
          @change_is_candidate="changeIsCandidate"
          @change_is_winner="changeIsWinner"
          @change_ng="changeNgAccount"
        ></InstagramPostTable>
        <InstagramPostGrid
          v-if="isGridView"
          :posts="posts"
          :options="options"
          @paging="onPaging"
          @change_is_widget="changeIsWidget"
          @change_is_candidate="changeIsCandidate"
          @change_is_winner="changeIsWinner"
          @change_ng="changeNgAccount"
        ></InstagramPostGrid>
      </div>
    </el-card>
  </div>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import moment from 'moment'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import CsvDownload from '@/client/components/_atoms/csv_download'
import api from '@/client/core/api'
import util from '@/client/core/util'
import CampaignAddWidgetDialog from '@/client/features/campaigns/components/campaign_add_widget_dialog'
import InstagramPostFilter from '@/client/features/instagram_post/instagram_post_filter'
import InstagramPostGrid from '@/client/features/instagram_post/instagram_post_grid'
import InstagramPostTable from '@/client/features/instagram_post/instagram_post_table'
import { formatDatetime, formatNumber } from '@/client/filters/filters'
import { TrackingService } from '@/client/services'
import { useInstagramPostsStore } from '@/client/store/useInstagramPostsStore'
import { useNavbarStore } from '@/client/store/useNavbarStore'

export default defineComponent({
  components: {
    CsvDownload,
    InstagramPostFilter,
    InstagramPostTable,
    InstagramPostGrid,
    CampaignAddWidgetDialog
  },
  data: () => {
    return {
      loading: false,
      loadingText: '',
      viewType: 'list',
      sortGrid: '-publishDatetime',
      sortGridOptions: [
        { value: '-publishDatetime', label: '日時(新しい順)' },
        { value: 'publishDatetime', label: '日時(古い順)' }
      ]
    }
  },
  computed: {
    ...mapState(useInstagramPostsStore, {
      error: state => state.error,
      firstLoaded: state => state.firstLoaded,
      params: state => state.params,
      entryCount: state => state.entryCount,
      posts: state => state.posts,
      prizes: state => state.prizes,
      campaign: state => {
        if (state.campaign) {
          return state.campaign
        }

        return {}
      },
      options: state => {
        return {
          total: state.total,
          pagingNo: state.params.pagingNo,
          limitCount: state.params.limitCount,
          sortBy: state.params.sortBy
        }
      },

      isListView() {
        return this.viewType === 'list'
      },

      isGridView() {
        return this.viewType === 'grid'
      },

      isLoading: state => state.loading
    }),
    isLoaded() {
      return !this.error && this.firstLoaded
    },

    lastProcessedDatetime() {
      if (!this.campaign.lastProcessedDatetime) {
        return '-'
      }

      return moment(this.campaign.lastProcessedDatetime).format('YYYY年MM月DD日 HH:mm')
    },

    widgetModified() {
      if (this.campaign.isWidgetCreated === 0) {
        return 'ウィジェット未作成'
      }

      if (!this.campaign.widgetModified) {
        return '-'
      }

      return moment(this.campaign.widgetModified).format('YYYY年MM月DD日 HH:mm')
    }
  },
  async created() {
    const params = {
      campaignId: this.$route.params.campaignId
    }

    this.setParams(params)

    await this.fetchCampaign()
    await this.fetchPrizes()

    this.setBreadcrumb([
      {
        name: 'キャンペーン一覧',
        link: '/campaigns'
      },
      {
        name: this.campaign.title,
        link: `/campaigns/${this.campaign.id}/instagram_posts`,
        iconName: this.campaign.account ? this.campaign.account.name : '削除されたアカウント',
        iconUrl: this.campaign.account ? this.campaign.account.pictureUrl : null,
        iconType: this.campaign.snsType,
        isIcon: true
      },
      {
        name: '応募履歴',
        link: null
      }
    ])
  },
  watch: {
    params() {
      this.fetchPosts()
    }
  },
  methods: {
    formatNumber,

    ...mapActions(useInstagramPostsStore, [
      'fetchPosts',
      'fetchCampaign',
      'fetchPrizes',
      'setParams',
      'reset',
      'updateIsWidget',
      'updateIsCandidate',
      'updateIsWinner',
      'updateNgAccount'
    ]),
    ...mapActions(useNavbarStore, ['setBreadcrumb']),

    /**
     * ウィジェットフラグを変更
     */
    async changeIsWidget(payload: { postId: string; isWidget: number }) {
      const result = await this.updateIsWidget(payload)

      if (!result || !result.data) {
        this.$notify({
          title: 'ウィジェットの保存に失敗しました。',
          customClass: 'danger',
          duration: 5000
        })
      }
    },

    /**
     * 候補者フラグを変更
     */
    async changeIsCandidate(payload: { applicantId: number; isCandidate: number }) {
      const result = await this.updateIsCandidate(payload)

      if (!result || !result.data) {
        this.$notify({
          title: '候補者の保存に失敗しました',
          customClass: 'danger',
          duration: 5000
        })
      }
    },

    /**
     * 当選者フラグを変更
     */
    async changeIsWinner(payload: { prizeId: number; applicantId: number; isWinner: number }) {
      const result = await this.updateIsWinner(payload)

      if (result.error && result.error.type === 'MAX_WINNER_COUNT_OVER') {
        this.$notify({
          title: '残り当選数に空きがありません。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      if (!result || !result.data) {
        this.$notify({
          title: '当選者の保存に失敗しました',
          customClass: 'danger',
          duration: 5000
        })
      }
    },

    /**
     * ビュータイプの変更
     */
    changeViewType(value: string) {
      TrackingService.sendEvent(`click:応募履歴（IG）|${value === 'list' ? '一覧' : 'グリッド'}`)

      this.viewType = value
    },

    /**
     * 応募履歴一覧をCSVダウンロードできる
     * @returns {Promise<void>}
     */
    async downloadCsv() {
      TrackingService.sendEvent('click:応募履歴（IG）|CSVダウンロード')

      const posts = await this.getApplicantsForCsv()
      if (!posts.length) {
        return { error: 'ERROR_LOADING' }
      }

      const filename = `campaign_posts_${this.campaign.title}_${moment().format('YYYY_MM_DD')}`
      const csvData = []

      csvData.push([
        '応募日時',
        '応募グループ',
        'アカウント名',
        'アカウントURL',
        '投稿内容',
        '投稿画像URL',
        '投稿URL',
        'フォロー中',
        'NGアカウント',
        'ウィジェット表示',
        '候補者',
        '当選者'
      ])

      // CSVデータを格納
      posts.forEach(post => {
        csvData.push([
          formatDatetime(post.publishDatetime),
          post.prizeName,
          post.screenName,
          'https://www.instagram.com/' + post.screenName,
          post.message ? post.message.replace(/(\r\n|\n|\r)/gm, '') : '',
          post.mediaUrl,
          post.permalink,
          post.isFollowing,
          post.isNg,
          post.isWidget,
          post.isCandidate,
          post.isWinner
        ])
      })

      return {
        filename,
        data: csvData
      }
    },

    /**
     * CSV用応募履歴一覧を取得
     */
    async getApplicantsForCsv() {
      this.loading = true
      const { total } = this.options

      this.getTextLoading(0, total)

      const params = Object.assign({}, this.params, {
        pagingNo: 1,
        limitCount: 1000
      })

      let totalResults = []
      // 1000件ごとに取得する
      for (let count = 0; count < total; count += 1000) {
        const result = await api.get('/instagram_posts', { params })

        const surplus = (total - count) / 1000

        if (surplus < 1) {
          this.getTextLoading(total, total)
        } else {
          this.getTextLoading(count + 1000, total)
        }

        if (!result.data) {
          this.loading = false
          return []
        }

        totalResults = totalResults.concat(result.data.posts)
        params.pagingNo = params.pagingNo + 1
      }

      this.loading = false
      return totalResults
    },

    /**
     * ページを変更イベント
     * @param {number} page ページ
     */
    onPaging(payload: { pagingNo: number; limitCount: number }) {
      this.setParams(payload)
      util.scrollToTop()
    },

    /**
     * ソートを変更イベント
     * @param {string} sortBy ソート
     */
    onSorting(value: string) {
      if (this.params.sortBy === value) {
        return
      }

      this.sortGrid = value

      this.setParams({ sortBy: value })
    },

    /**
     * CSVダウンロードの時、進捗を表示する
     * @params {number} acquiredNumber
     * @params {number} totalNumber
     */
    getTextLoading(acquiredNumber: number, totalNumber: number) {
      this.loadingText = `CSVファイルの作成中です。しばらくお待ちください。\n ${acquiredNumber}件 / ${totalNumber}件`
    },

    /**
     * 戻る
     */
    back() {
      TrackingService.sendEvent('click:応募履歴（IG）|戻る')

      this.$router.push('/instagram_campaigns/' + this.$route.params.campaignId + '/prizes')
    },

    openCampaignAddWiddetDialog() {
      TrackingService.sendEvent('click:応募履歴（IG）|ウィジェット管理')

      this.$refs.campaignAddWidgetDialog.open()
    },

    /**
     * NGアカウントフラグを変更
     */
    async changeNgAccount(payload: { screenName: string; isNg: boolean; ngApplicantId: number | null }) {
      const result = await this.updateNgAccount(payload)

      if (!result || !result.data) {
        this.$notify({
          title: 'NGアカウントの更新に失敗しました。',
          customClass: 'danger',
          duration: 5000
        })
      }
    }
  },
  beforeRouteLeave(to: any, from: any, next: any) {
    // キャンペーンストアをリセット
    this.reset()
    next()
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.instagram-post :deep(.csv-loading) {
  .el-loading-spinner {
    top: 40vh;

    .el-loading-text {
      white-space: pre-line;
    }
  }
}

.message-center {
  text-align: center;
}

.wait-image {
  max-width: $spacing-unit * 13;
}
</style>
